import { EllipsisOutlined } from "@ant-design/icons";
import { Button, Menu, notification, Popover } from "antd";
import React, { useState } from "react";
import { EditOrderAttachmentsModal } from "../EditOrderAttachementsModal/EditOrderAttachmentsModal";
import CoverWizardModal, {
  CoverWizardForm,
  CoverWizardModalPayload,
  emptyCoverQuoteInitialValues,
} from "./CoverWizardModal/CoverWizardModal";
import OrderDto from "../../../store/actions/dtos/OrderDto";
import { getOrderDetails } from "../../../store/actions/orderFlowActionCreators";
import { SubmitCoverWizardDto } from "../../../store/actions/dtos/SubmitCoverWizardDto";
import moment from "moment";
import { css } from "@emotion/css";

export function payloadToFormModel(
  payload: SubmitCoverWizardDto
): Omit<CoverWizardForm, "fileList" | "measurementsDocuments" | "date"> {
  return {
    quoteType: payload.quoteType,
    poNumber: !!payload.poNumber
      ? payload.poNumber
      : emptyCoverQuoteInitialValues.poNumber,
    jobName: !!payload.jobName
      ? payload.jobName
      : emptyCoverQuoteInitialValues.jobName,
    // Pool Owner Info
    address: !!payload.poolOwnerInfo?.address
      ? payload.poolOwnerInfo?.address
      : emptyCoverQuoteInitialValues.address,
    city: !!payload.poolOwnerInfo?.city
      ? payload.poolOwnerInfo?.city
      : emptyCoverQuoteInitialValues.city,
    stateCode: !!payload.poolOwnerInfo?.stateCode
      ? payload.poolOwnerInfo?.stateCode
      : emptyCoverQuoteInitialValues.stateCode,
    zip: payload.poolOwnerInfo?.zip,
    // Shipping Info
    shippingName: !!payload.shippingInfo?.shippingName
      ? payload.shippingInfo?.shippingName
      : emptyCoverQuoteInitialValues.shippingName,
    shippingAddress: !!payload.shippingInfo?.shippingAddress
      ? payload.shippingInfo?.shippingAddress
      : emptyCoverQuoteInitialValues.shippingAddress,
    shippingCity: !!payload.shippingInfo?.shippingCity
      ? payload.shippingInfo?.shippingCity
      : emptyCoverQuoteInitialValues.shippingCity,
    shippingStateCode: !!payload.shippingInfo?.shippingStateCode
      ? payload.shippingInfo?.shippingStateCode
      : emptyCoverQuoteInitialValues.shippingStateCode,
    shippingZip: !!payload.shippingInfo?.shippingZip
      ? payload.shippingInfo?.shippingZip
      : emptyCoverQuoteInitialValues.shippingZip,
    // File Attachment Info
    comments: !!payload.fileAttachmentInfo?.comments
      ? payload.fileAttachmentInfo?.comments
      : emptyCoverQuoteInitialValues.comments,
    measureWithMeyco: !!payload.fileAttachmentInfo?.measureWithMeyco
      ? payload.fileAttachmentInfo?.measureWithMeyco
      : emptyCoverQuoteInitialValues.measureWithMeyco,
    distanceFromAB: !!payload.fileAttachmentInfo?.distanceFromAB
      ? payload.fileAttachmentInfo?.distanceFromAB
      : emptyCoverQuoteInitialValues.distanceFromAB,
    // Cover Info
    coverSpacing: !!payload?.coverInfo?.coverSpacing
      ? payload?.coverInfo?.coverSpacing
      : emptyCoverQuoteInitialValues.coverSpacing,
    coverMaterial: !!payload?.coverInfo?.coverMaterial
      ? payload?.coverInfo?.coverMaterial
      : emptyCoverQuoteInitialValues.coverMaterial,
    coverColor: !!payload?.coverInfo?.coverColor
      ? payload?.coverInfo?.coverColor
      : emptyCoverQuoteInitialValues.coverColor,
    // Autocover Info
    autocoverNeedsToBeCovered: !!payload?.coverInfo?.autocoverInfo
      ?.autocoverNeedsToBeCovered
      ? "yes"
      : "no",
    autocoverBoxWidth: !!payload?.coverInfo?.autocoverInfo?.autocoverBoxWidth
      ? payload?.coverInfo?.autocoverInfo?.autocoverBoxWidth
      : emptyCoverQuoteInitialValues.autocoverBoxWidth,
    autocoverDeckType: !!payload?.coverInfo?.autocoverInfo?.autocoverDeckType
      ? payload?.coverInfo?.autocoverInfo?.autocoverDeckType
      : emptyCoverQuoteInitialValues.autocoverDeckType,
    autocoverWidthBehindBox: !!payload?.coverInfo?.autocoverInfo
      ?.autocoverWidthBehindBox
      ? payload?.coverInfo?.autocoverInfo?.autocoverWidthBehindBox
      : emptyCoverQuoteInitialValues.autocoverWidthBehindBox,
    // Pool Details
    hasNonRemovableRails: !!payload.poolDetails?.hasNonRemovableRails
      ? "yes"
      : "no",
    nonRemovableRailsLocation: !!payload.poolDetails?.nonRemovableRailsLocation
      ? payload.poolDetails?.nonRemovableRailsLocation
      : emptyCoverQuoteInitialValues.nonRemovableRailsLocation,
    hasSpoutOnDeck: !!payload.poolDetails?.hasSpoutOnDeck ? "yes" : "no",
    spoutLocation: !!payload.poolDetails?.spoutLocation
      ? payload.poolDetails?.spoutLocation
      : emptyCoverQuoteInitialValues.spoutLocation,
    fillSpoutHeight: !!payload.poolDetails?.fillSpoutHeight
      ? payload.poolDetails?.fillSpoutHeight
      : emptyCoverQuoteInitialValues.fillSpoutHeight,
    fillSpoutBelowCoping: !!payload.poolDetails?.fillSpoutBelowCoping
      ? "yes"
      : "no",
    fillSpoutBelowCopingLocation: !!payload.poolDetails
      ?.fillSpoutBelowCopingLocation
      ? payload.poolDetails?.fillSpoutBelowCopingLocation
      : emptyCoverQuoteInitialValues.fillSpoutBelowCopingLocation,
    hasRadiusedCorners: !!payload.poolDetails?.hasRadiusedCorners
      ? "yes"
      : "no",
    cornerRadius: !!payload.poolDetails?.cornerRadius
      ? payload.poolDetails?.cornerRadius
      : emptyCoverQuoteInitialValues.cornerRadius,
    hasCutCorners: !!payload.poolDetails?.hasCutCorners ? "yes" : "no",
    cutCornersRadius: !!payload.poolDetails?.cutCornersRadius
      ? payload.poolDetails?.cutCornersRadius
      : emptyCoverQuoteInitialValues.cutCornersRadius,
    hasVanishingEdge: !!payload.poolDetails?.hasVanishingEdge ? "yes" : "no",
    copingToVanishingEdge: !!payload.poolDetails?.copingToVanishingEdge
      ? payload.poolDetails?.copingToVanishingEdge
      : emptyCoverQuoteInitialValues.copingToVanishingEdge,
    vanishingEdgeWidth: !!payload.poolDetails?.vanishingEdgeWidth
      ? payload.poolDetails?.vanishingEdgeWidth
      : emptyCoverQuoteInitialValues.vanishingEdgeWidth,
    catchBasinCovered: !!payload.poolDetails?.catchBasinCovered ? "yes" : "no",
    separateCatchBasinCover: !!payload.poolDetails?.separateCatchBasinCover
      ? "yes"
      : "no",
    wallToCatchBasinHeight: payload.poolDetails?.wallToCatchBasinHeight,
    slideOrRockBelowCoping: payload.poolDetails?.slideOrRockBelowCoping,
    slideOrRockBelowCopingHeight:
      payload.poolDetails?.slideOrRockBelowCopingHeight,
    hasSunLedge: payload.poolDetails?.hasSunLedge ? "yes" : "no",
    // Spa Info
    spaCoverWanted: payload.spaInfo ? "yes" : "no",
    spaWallOrientation: payload.spaInfo?.spaWallOrientation ?? "",
    spaPoolHeightDifference: !!payload.spaInfo?.spaPoolHeightDifference
      ? payload.spaInfo?.spaPoolHeightDifference
      : emptyCoverQuoteInitialValues.spaPoolHeightDifference,
    spaHasOpenSpillway: !!payload.spaInfo?.spaHasOpenSpillway ? "yes" : "no",
    spaSpillwayWidthLocation: !!payload.spaInfo?.spaSpillwayWidthLocation
      ? payload.spaInfo?.spaSpillwayWidthLocation
      : emptyCoverQuoteInitialValues.spaSpillwayWidthLocation,
    spaCopingSize: !!payload.spaInfo?.spaCopingSize
      ? payload.spaInfo?.spaCopingSize
      : emptyCoverQuoteInitialValues.spaCopingSize,
    spaCopingOd: !!payload.spaInfo?.spaCopingOd
      ? payload.spaInfo?.spaCopingOd
      : emptyCoverQuoteInitialValues.spaCopingOd,
    spaCopingId: !!payload.spaInfo?.spaCopingId
      ? payload.spaInfo?.spaCopingId
      : emptyCoverQuoteInitialValues.spaCopingId,
    spaDesignPreferences: !!payload.spaInfo?.spaDesignPreferences
      ? payload.spaInfo?.spaDesignPreferences
      : emptyCoverQuoteInitialValues.spaDesignPreferences,
    // Decking Info
    hasDeck: !!payload.deckingInfo ? "yes" : "no",
    deckingTotalRaise: !!payload.deckingInfo?.deckingTotalRaise
      ? payload.deckingInfo?.deckingTotalRaise
      : emptyCoverQuoteInitialValues.deckingTotalRaise,
    deckingPtA: !!payload.deckingInfo?.deckingPtA
      ? payload.deckingInfo?.deckingPtA
      : emptyCoverQuoteInitialValues.deckingPtA,
    deckingHeightA: !!payload.deckingInfo?.deckingHeightA
      ? payload.deckingInfo?.deckingHeightA
      : emptyCoverQuoteInitialValues.deckingHeightA,
    deckingPtB: !!payload.deckingInfo?.deckingPtB
      ? payload.deckingInfo?.deckingPtB
      : emptyCoverQuoteInitialValues.deckingPtB,
    deckingHeightB: !!payload.deckingInfo?.deckingHeightB
      ? payload.deckingInfo?.deckingHeightB
      : emptyCoverQuoteInitialValues.deckingHeightB,
    deckingAnchorMethod: !!payload.deckingInfo?.deckingAnchorMethod
      ? payload.deckingInfo?.deckingAnchorMethod
      : emptyCoverQuoteInitialValues.deckingAnchorMethod,
    deckingWallHardware: !!payload.deckingInfo?.deckingWallHardware
      ? payload.deckingInfo?.deckingWallHardware
      : emptyCoverQuoteInitialValues.deckingWallHardware,
    deckingIncludeCable: !!payload.deckingInfo?.deckingIncludeCable
      ? payload.deckingInfo?.deckingIncludeCable
      : emptyCoverQuoteInitialValues.deckingIncludeCable,
    hasAutocover: !!payload?.coverInfo?.autocoverInfo ? "yes" : "no",
    useMeasuringServices: payload.fileAttachmentInfo?.measureWithMeyco,
  };
}

const popoverClass = css`
  .ant-popover-inner-content {
    padding: 3px 6px 3px 6px;
    border: 1px solid #949494;
    border-radius: 8px;
  }
`;

export default function OrderContextMenu({
  order,
  hideEditAttachments,
  reloadOrders,
  cloneOrderButton,
  coverWizardModalPayloadState: [
    coverWizardModalPayload,
    setCoverWizardModalPayload,
  ],
  editAttachmentsModalPayloadState: [
    editAttachmentsModalPayload,
    setEditAttachmentsModalPayload,
  ],
}: {
  hideEditAttachments: boolean;
  order: OrderDto;
  reloadOrders: () => void;
  coverWizardModalPayloadState: [
    CoverWizardModalPayload | null,
    React.Dispatch<React.SetStateAction<CoverWizardModalPayload | null>>
  ];
  editAttachmentsModalPayloadState: [
    OrderDto | null,
    React.Dispatch<React.SetStateAction<OrderDto | null>>
  ];
  cloneOrderButton: boolean;
}) {
  const [open, setOpen] = useState<boolean>(false);

  async function openAutofilledCoverWizard(quoteType: string) {
    const [orderDetails, orderDetailsError] = await getOrderDetails(order.soNo);
    if (!orderDetails || orderDetailsError) return;
    const mappedPayload = payloadToFormModel(orderDetails.data);
    const payload = {
      ...mappedPayload,
      date: moment(),
      fileList: [],
      measurementsDocuments: [],
      quoteType,
    };
    setCoverWizardModalPayload({
      initialValues: payload,
      startWithOrderTypePage: false,
    });
  }

  return (
    <>
      <Popover
        overlayClassName={popoverClass}
        //this style is not working in the popover class
        overlayStyle={{ paddingTop: "0px" }}
        trigger="click"
        open={open}
        onOpenChange={setOpen}
        placement="bottomRight"
        showArrow={false}
        content={
          <Menu>
            <Menu.Item
              key="edit-submission"
              hidden={hideEditAttachments}
              onClick={() => {
                setEditAttachmentsModalPayload(order);
                setOpen(false);
              }}
            >
              Edit/Update Submission
            </Menu.Item>
            <Menu.Item
              key="add-answer-and-information-request"
              hidden={hideEditAttachments}
              onClick={() => {
                setEditAttachmentsModalPayload(order);
                setOpen(false);
              }}
            >
              Add Answer and Information request
            </Menu.Item>

            <Menu.Item
              hidden={!cloneOrderButton}
              key="reorder"
              onClick={() => {
                openAutofilledCoverWizard("Re-Order");
                setOpen(false);
              }}
            >
              Re-Order
            </Menu.Item>
          </Menu>
        }
      >
        <Button
          type="primary"
          icon={<EllipsisOutlined />}
          onClick={(event) => {
            setOpen(!open);
          }}
        />
      </Popover>
    </>
  );
}
